// src/plugins/permissions.js
import axios from 'axios';
import { reactive } from 'vue';

const state = reactive({
  role: null,
  roleLoaded: false,
  checking: false,
  lastChecked: 0,
});

const ROLE_COOKIE_NAME = 'almacen_role_checked_ts';
const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_HOUR_SECONDS = 60 * 60;

// Helpers de cookies
function getCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(
    new RegExp(
      '(?:^|; )' +
        name.replace(/([$?*|{}()[\]\\/+^])/g, '\\$1') +
        '=([^;]*)'
    )
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name, value, maxAgeSeconds) {
  if (typeof document === 'undefined') return;
  let cookie = `${name}=${encodeURIComponent(String(value))}; path=/;`;
  if (typeof maxAgeSeconds === 'number') {
    cookie += ` Max-Age=${maxAgeSeconds};`;
  }
  document.cookie = cookie;
}

function clearCookie(name) {
  setCookie(name, '', -1);
}

function getLastCheckTimestamp() {
  const raw = getCookie(ROLE_COOKIE_NAME);
  if (!raw) return null;
  const ts = parseInt(raw, 10);
  return Number.isFinite(ts) ? ts : null;
}

function setLastCheckTimestamp(timestamp) {
  setCookie(ROLE_COOKIE_NAME, timestamp, ONE_HOUR_SECONDS);
}

// Carga/valida el rol, con cache en cookie de 1 hora
async function loadRole({ store, force = false } = {}) {
  if (!store) {
    console.warn('[permissions] No se proporcionó store a permissionsPlugin.');
    state.role = null;
    state.roleLoaded = true;
    return;
  }

  if (state.checking) return;

  const now = Date.now();
  const lastCheck = getLastCheckTimestamp();
  const roleInStore = store.state.role;

  // Si tenemos rol en el store y la cookie dice que sigue fresco (<1h), lo usamos
  if (!force && roleInStore && lastCheck && now - lastCheck < ONE_HOUR_MS) {
    state.role = roleInStore;
    state.roleLoaded = true;
    state.lastChecked = lastCheck;
    return;
  }

  const roleToken = store.state.roleToken;
  const userToken = store.state.user && store.state.user.token;

  if (!roleToken || !userToken) {
    state.role = null;
    state.roleLoaded = true;
    state.lastChecked = 0;
    clearCookie(ROLE_COOKIE_NAME);
    return;
  }

  try {
    state.checking = true;

    const { data } = await axios.post('/api/verify-role', {
      roleToken,
      userToken,
    });

    const { role, newRoleToken } = data || {};

    if (!role) {
      throw new Error('Respuesta de rol inválida');
    }

    // Normalizamos a minúsculas por si acaso (back/front consistentes)
    const normalizedRole = String(role).toLowerCase();

    // Actualizamos estado local + cookie
    state.role = normalizedRole;
    state.roleLoaded = true;
    state.lastChecked = now;
    setLastCheckTimestamp(now);

    // Actualizamos Vuex para que todo el app esté alineado
    if (newRoleToken) {
      store.commit('setRoleToken', newRoleToken);
    }
    store.commit('setRole', normalizedRole);
  } catch (error) {
    console.error('[permissions] Error al verificar rol:', error);
    state.role = null;
    state.roleLoaded = true;
    state.lastChecked = 0;
    clearCookie(ROLE_COOKIE_NAME);
    if (store && typeof store.commit === 'function') {
      try {
        store.commit('clearRole');
      } catch (e) {
        console.warn('[permissions] No se pudo limpiar el rol en el store:', e);
      }
    }
  } finally {
    state.checking = false;
  }
}

/**
 * Lógica de "¿puede ver?" SIN jerarquía.
 *
 * - Si el rol actual está entre los roles esperados → true
 * - Si no hay roles esperados (no pasas nada) → true (comportamiento "libre")
 * - Si el rol no coincide exactamente con ninguno → false
 */
function canView(...expectedRoles) {
  if (!state.roleLoaded) return false; // mientras no sepamos, jugamos seguro
  if (!state.role) return false;

  // Si no se pasa ningún rol esperado, lo consideramos "sin restricción"
  if (!expectedRoles || expectedRoles.length === 0) return true;

  const current = String(state.role).toLowerCase();

  // Normalizamos expectedRoles a minúsculas para comparar
  const normalizedExpected = expectedRoles
    .filter(Boolean)
    .map((r) => String(r).toLowerCase());

  return normalizedExpected.includes(current);
}

// Para Composition API
export function usePermissions() {
  return {
    roleState: state,
    canView,
    reloadRole: (store, force = true) => loadRole({ store, force }),
  };
}

// Plugin para Options API + global
export default {
  install(app, options = {}) {
    const store =
      options.store ||
      app.config.globalProperties.$store ||
      (options.getStore && options.getStore());

    if (store) {

      // ✅ Solo refresco silencioso cada hora
      setInterval(() => {
        loadRole({ store, force: true });
      }, ONE_HOUR_MS);
    } else {
      console.warn(
        '[permissions] No se encontró un store de Vuex. Asegúrate de pasar { store } al usar el plugin.'
      );
      state.roleLoaded = true;
    }

    // this.$role y this.$canView en Options API
    app.config.globalProperties.$role = state;
    app.config.globalProperties.$canView = (...roles) => canView(...roles);

    // provide/inject para Composition API
    app.provide('permissions', {
      roleState: state,
      canView,
      reloadRole: (force = true) =>
        store ? loadRole({ store, force }) : Promise.resolve(),
    });
  },
};
